// @ts-strict-ignore
import {Event} from 'common/query-builder/query-block/events-menu/types';
import {Segment} from 'common/query-builder/query-block/segment-menu/types';
import {ComparisonDate} from 'common/query-builder/query-block/time-comparison-menu/types';
import {PropertyType, ResourceType} from 'common/report/constants';
import {getEventValueAndLabel} from 'common/report/util/events';
import {Segmentation} from 'common/types/reports/query';
import {Bucket, StringBucket} from 'common/widgets/bucket-pane/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS} from 'common/widgets/date-range-picker/models/presets';
import {DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {BooleanOperator, FilterOperator, StringOperator} from 'common/widgets/property-filter-menu/models/types';
import {BEHAVIOR_AGGREGATION, BEHAVIOR_FREQUENCY} from 'common/query-builder/query-block/properties-menu/constants';
import {FiltersOperator} from 'common/query-builder/query-entry-section/types';
import {LiftType} from 'common/report/util/lift/types';
import {LiftComparison} from 'common/widgets/lift-comparison-menu/types';
import {InsightsResourceType, PRIMITIVE_MATH_TYPES} from 'common/types/reports/bookmark';

import {AIBreakdown, AIEvent, AIPropertyFilter, AIQuery, CustomBuckets, TimeComparison} from './types';

/**
 * Converts AI time comparison to bookmark range.
 */
export function getTimeComparisonParam(aiTimeComparison: TimeComparison): ComparisonDate | null {
  if (!aiTimeComparison) {
    return null;
  }

  if (aiTimeComparison.type === `absolute-start`) {
    return {from: aiTimeComparison.date};
  }

  if (aiTimeComparison.type === `absolute-end`) {
    return {to: aiTimeComparison.date};
  }

  return {
    offset: {
      unit: aiTimeComparison.unit as TimeUnit,
      value: 1,
    },
  };
}

/**
 * Extracts common event params from an AI event.
 */
export function getCommonEventParams(aiEvent: AIEvent): Event {
  const isCustom = !!aiEvent.customEventId;

  return {
    ...getEventValueAndLabel({
      name: aiEvent.eventName,
      custom: isCustom,
      id: aiEvent.customEventId,
    }),
    id: aiEvent.customEventId,
    custom: isCustom,
  };
}

/**
 * Convert AI property filter to PropertyFilter instances.
 */
export function getPropertyFilters(aiFilters: AIPropertyFilter[]): PropertyFilter[] {
  const nullValueOperators: FilterOperator[] = [StringOperator.Set, StringOperator.NotSet];

  return (aiFilters || []).map((filter) => {
    let filterValue: any =
      filter.type === PropertyType.String && filter.operator !== StringOperator.Contains
        ? [filter.value]
        : filter.value;

    if (nullValueOperators.includes(filter.operator)) {
      filterValue = null;
    }

    // make sure using correct value for boolean ops
    if (filter.operator === BooleanOperator.True) {
      filterValue = true;
    } else if (filter.operator === BooleanOperator.False) {
      filterValue = false;
    }

    return new PropertyFilter({
      resourceType: filter.resource,
      propertyName: filter.property,
      propertyDefaultType: filter.type,
      propertyType: filter.type,
      filterValue,
      filterOperator: filter.operator,
    });
  });
}

/**
 * Convert AI breakdowns to segments.
 */
export function getBreakdownSegments(aiQuery: AIQuery): Segment[] {
  const hasBreakdowns = `breakdowns` in aiQuery && aiQuery.breakdowns;

  if (!hasBreakdowns) {
    return [];
  }

  return aiQuery.breakdowns.map(({metric, buckets}) => {
    let attrs: Segmentation;

    switch (metric.type) {
      case `property`:
        attrs = {
          resourceType: metric.property.resource,
          propertyType: metric.property.type,
          propertyDefaultType: metric.property.type,
          propertyName: metric.property.property,
        };
        break;
      case `frequency-per-user`:
        attrs = {
          resourceType: ResourceType.User,
          propertyType: PropertyType.Number,
          propertyName: ``,
          propertyDefaultType: PropertyType.Number,
          behavior: {
            aggregationOperator: PRIMITIVE_MATH_TYPES.TOTAL,
            behaviorType: BEHAVIOR_FREQUENCY.value,
            event: getCommonEventParams(metric.event),
            filters: [],
            filtersOperator: FiltersOperator.And,
          },
        };
        break;
      case `aggregate-property-per-user`:
        attrs = {
          resourceType: ResourceType.User,
          propertyType: PropertyType.Number,
          propertyName: ``,
          propertyDefaultType: PropertyType.Number,
          behavior: {
            aggregationOperator: metric.math,
            behaviorType: BEHAVIOR_AGGREGATION.value,
            event: getCommonEventParams(metric.event),
            property: {
              propertyDefaultType: metric.property.type,
              resourceType: metric.property.resource,
              type: metric.property.type,
              value: metric.property.property,
              label: metric.property.property,
            },
            filters: [],
            filtersOperator: FiltersOperator.And,
          },
        };
        break;
    }

    const segmentation: Segmentation = {
      ...attrs,
      customBucket: getCustomBucketsParam(buckets),
      customGroups: getCustomGroupsParam(buckets),
    };

    // the typing hates this but its right.. and better than duplicating the possible options
    return {
      [segmentation.resourceType]: segmentation,
    } as any;
  });
}

/**
 * Get report date range from AI query.
 */
export function getDateRange(aiQuery: AIQuery): DateRange {
  const dateRange = aiQuery.dateRange;

  if (dateRange) {
    if (dateRange.type === `relative`) {
      const {range} = dateRange;

      if (typeof range === `string`) {
        const presetParams = PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[range];

        if (presetParams) {
          return new DateRange(presetParams);
        }
      } else if (typeof range === `object`) {
        return new DateRange({
          type: DateRangeType.RelativeAfter,
          window: {
            unit: range.unit,
            value: range.value,
          },
        });
      }
    } else {
      return new DateRange({
        type: DateRangeType.Between,
        from: dateRange.from,
        to: dateRange.to,
      });
    }
  }

  // default to last 30 days
  return new DateRange(PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[`last_30_days`]);
}

/**
 * Convert AI breakdown numeric buckets to bookmark params.
 */
function getCustomBucketsParam(aiBuckets: CustomBuckets): Bucket | null {
  if (aiBuckets && `size` in aiBuckets) {
    return {
      bucketSize: aiBuckets.size || 10,
      max: aiBuckets.max || 100,
      min: aiBuckets.min || 0,
      // null is a valid value
      // funnels don't work if we send an empty array
      ...(aiBuckets.intervals?.length ? {groups: aiBuckets.intervals} : {}),
    };
  }

  return null;
}

/**
 * Convert AI breakdown string buckets to bookmark params.
 */
function getCustomGroupsParam(aiBuckets: CustomBuckets): StringBucket | null {
  if (aiBuckets && `groups` in aiBuckets) {
    return aiBuckets.groups.map((group) => ({
      filterOperator: group.operator as StringOperator,
      filterValue: group.values,
      renamed: null,
    }));
  }

  return null;
}

/**
 * Checks if the last breakdown has a 'useAsBaseSegment' property.
 *
 * @returns {LiftComparison} - The lift comparison param if found, null otherwise.
 */
export function getLiftComparisonParam(aiBreakdowns: AIBreakdown[], isInsights?: boolean): LiftComparison {
  // we check only the last breakdown, since it is the only one that can be used for comparison
  const breakdown = aiBreakdowns?.length ? aiBreakdowns[aiBreakdowns.length - 1] : null;

  if (breakdown?.useAsBaseSegment && `property` in breakdown.metric) {
    const property = breakdown.metric.property;
    // small hack for insights, it uses "events","people" instead of "event", "User"
    let resourceType = property.resource as any;
    if (isInsights) {
      resourceType =
        property.resource === ResourceType.Event ? InsightsResourceType.Events : InsightsResourceType.People;
    }

    return {
      type: LiftType.Segment,
      groupValue: breakdown.useAsBaseSegment,
      comparisonProperty: {
        resourceType,
        propertyName: property.property,
        propertyType: property.type,
      },
    };
  }
  return null;
}

/**
 *
 * Makes adjustments to the AI query to catch possible format errors early.
 */
export function patchAIQuery(aiQuery: AIQuery) {
  const breakdowns = (`breakdowns` in aiQuery ? aiQuery.breakdowns : []) || [];

  // Segment comparison breakdown must be the last one
  for (let i = 0; i < breakdowns.length; i++) {
    const breakdown = breakdowns[i];
    // if it is not the last breakdown, we move it to the end
    if (breakdown.useAsBaseSegment && i !== breakdowns.length - 1) {
      breakdowns.splice(i, 1);
      breakdowns.push(breakdown);
      break;
    }
  }

  // buckets groups must be an array
  for (const breakdown of breakdowns) {
    if (breakdown.buckets && `groups` in breakdown.buckets && breakdown.buckets.groups) {
      // check if groups is an array, if it isn't, we make it one
      breakdown.buckets.groups = Array.isArray(breakdown.buckets.groups)
        ? breakdown.buckets.groups
        : [breakdown.buckets.groups];
    }
  }
}
